// src/composables/useDynamicTaskEngine.js
import { computed } from "vue";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { useAssessmentStore } from "@/stores/assessmentStore";
import { useWeeklyTargetEngineCore } from "./useWeeklyTargetEngine";

const BAND_ORDER = ["beginner", "developing", "secure", "confident", "mastery"];
function bandIndex(b) { return BAND_ORDER.indexOf(b); }

/**
 * Dynamic Task Engine
 * -------------------
 * Generates personalized daily tasks based on:
 * - In-progress lessons (continue)
 * - Weak topics (accuracy < 50%)
 * - New lessons from curriculum
 * - Revision (spaced repetition)
 * - Quick quizzes
 * - Challenge tests
 * - Diagnostic check-ins
 */

const TASK_PRIORITIES = {
  continue: 1,      // Resume in-progress lesson
  weakTopic: 2,     // Topic below 50% accuracy
  newLesson: 3,     // Next curriculum lesson
  revision: 4,      // Spaced repetition review
  quickQuiz: 5,     // Short practice burst
  challenge: 6,     // Test/assessment
  diagnostic: 7     // Periodic check-in
};

const XP_BY_TASK = {
  continue: 25,
  weakTopic: 20,
  newLesson: 25,
  revision: 15,
  quickQuiz: 10,
  challenge: 30,
  diagnostic: 20
};

export function useDynamicTaskEngine(childId) {
  const progressStore = useProgressStore();
  const curriculumStore = useCurriculumStore();
  const assessmentStore = useAssessmentStore();
  const weekly = useWeeklyTargetEngineCore(childId);

  function isToday(ts) {
    const d = new Date(ts);
    const n = new Date();
    return d.toISOString().slice(0, 10) === n.toISOString().slice(0, 10);
  }

  const todayActivity = computed(() => {
    const log = progressStore.activityLog?.[childId] || [];
    return log.filter(e => isToday(e.timestamp));
  });

  const doneToday = computed(() => ({
    lesson: todayActivity.value.some(e => e.type === "lesson" || e.type === "topic"),
    revision: todayActivity.value.some(e => e.type === "revision"),
    test: todayActivity.value.some(e => e.type === "test"),
    diagnostic: todayActivity.value.some(e => e.type === "diagnostic")
  }));

  // Find the lesson that was completed today (for showing as a completed task)
  const completedLessonToday = computed(() => {
    const entry = todayActivity.value.find(e => e.type === "lesson" || e.type === "topic");
    if (!entry?.lessonId) return null;
    return curriculumStore.allLessons.find(l => l.id === entry.lessonId) || null;
  });

  // Find topics needing attention (accuracy < 0.5)
  const weakTopics = computed(() => {
    const progress = progressStore.progress?.[childId] || {};
    return Object.entries(progress)
      .filter(([, p]) => p.accuracy < 0.5 && p.attempts >= 2)
      .sort((a, b) => a[1].accuracy - b[1].accuracy)
      .slice(0, 3)
      .map(([sgId, p]) => ({ sgId, accuracy: p.accuracy }));
  });

  // Has the student practiced any topics yet?
  const hasPracticedTopics = computed(() => {
    const prog = progressStore.progress?.[childId] || {};
    return Object.keys(prog).some(k => k);
  });

  // Check if revision is due (no revision done in 3+ days)
  const revisionDue = computed(() => {
    // Check revisionHistory first
    const history = progressStore.revisionHistory?.[childId] || [];
    if (history.length) {
      const lastRevision = Math.max(...history.map(h => new Date(h.date).getTime() || 0));
      return Date.now() - lastRevision > 3 * 24 * 60 * 60 * 1000;
    }
    // Fallback: check activity log for any revision entry
    const log = progressStore.activityLog?.[childId] || [];
    const lastRevisionActivity = log.find(e => e.type === "revision");
    if (lastRevisionActivity) {
      return Date.now() - lastRevisionActivity.timestamp > 3 * 24 * 60 * 60 * 1000;
    }
    return true; // never done revision
  });

  // Helper to find section group info from curriculum
  function findSectionGroupInfo(sgId) {
    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          const sub = lesson.subsections?.find(s => s.content === sgId);
          if (sub) {
            return { title: sub.title || sgId, lessonId: lesson.id, unitTitle: unit.title };
          }
        }
      }
    }
    return null;
  }

  // Check if all topics in a lesson are at least "secure"
  function isLessonReady(lesson) {
    const subs = lesson.subsections || [];
    if (!subs.length) return false;
    for (const sub of subs) {
      const band = progressStore.getSectionGroupBand(childId, sub.content);
      if (bandIndex(band) < bandIndex("secure")) return false;
    }
    return true;
  }

  // Find the first lesson that was skipped — not completed, zero attempts,
  // and sits before the student's current position in the curriculum
  function getFirstSkippedLesson() {
    const continueLesson = progressStore.getContinueLesson(childId);
    if (!continueLesson?.lessonId) return null;

    const allLessons = curriculumStore.modules.flatMap(mod =>
      mod.units.flatMap(unit => unit.lessons.map(l => ({ ...l, unitTitle: unit.title })))
    );
    const continueLessonIdx = allLessons.findIndex(l => l.id === continueLesson.lessonId);
    if (continueLessonIdx <= 0) return null;

    const childProgress = progressStore.progress?.[childId] || {};
    for (let i = 0; i < continueLessonIdx; i++) {
      const lesson = allLessons[i];
      if (isLessonReady(lesson)) continue; // already completed
      const hasAttempts = lesson.subsections?.some(sub => {
        const p = childProgress[sub.content];
        return p && p.attempts > 0;
      });
      if (!hasAttempts) {
        return { lessonId: lesson.id, lessonTitle: lesson.title, unitTitle: lesson.unitTitle };
      }
    }
    return null;
  }

  // Find next uncompleted lesson from curriculum
  function getNextCurriculumLesson() {
    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          if (!isLessonReady(lesson)) {
            return {
              lessonId: lesson.id,
              lessonTitle: lesson.title,
              unitTitle: unit.title,
              reason: "Next in curriculum"
            };
          }
        }
      }
    }
    return null;
  }

  // Generate dynamic tasks (max 4)
  const tasks = computed(() => {
    const out = [];
    const remaining = weekly.remaining.value || { lessons: 5, revisions: 5, tests: 1 };

    // 1. Check continue pointer — show as completed if lesson is now ready
    const continueLesson = progressStore.getContinueLesson(childId);
    if (continueLesson?.lessonId) {
      const lesson = curriculumStore.allLessons.find(l => l.id === continueLesson.lessonId);
      if (lesson) {
        if (isLessonReady(lesson)) {
          // All topics at secure — lesson is genuinely complete
          out.push({
            type: "continue",
            priority: TASK_PRIORITIES.continue,
            xp: XP_BY_TASK.continue,
            stars: 3,
            title: lesson.title,
            subtitle: "Completed!",
            lessonId: lesson.id,
            route: `/student/${childId}/learn/${lesson.id}`,
            completed: true
          });
        } else {
          // Lesson still in progress. Show as pending whether or not they worked on it
          // today — "done today" does not mean "finished" if not all topics are at secure.
          // Exception: if they did a DIFFERENT lesson today, skip this and let 1b handle it.
          const practicedThisLessonToday =
            !doneToday.value.lesson ||
            completedLessonToday.value?.id === lesson.id;
          if (practicedThisLessonToday) {
            out.push({
              type: "continue",
              priority: TASK_PRIORITIES.continue,
              xp: XP_BY_TASK.continue,
              stars: 3,
              title: `Continue: ${lesson.title}`,
              subtitle: doneToday.value.lesson ? "Keep going — more topics to do!" : (lesson.unitTitle || "Resume where you left off"),
              lessonId: lesson.id,
              route: `/student/${childId}/learn/${lesson.id}`,
              completed: false
            });
          }
          // else: they did a different lesson today — section 1b will show it
        }
      }
    }

    // 1b. If a lesson was done today but not yet shown (e.g. continueLesson advanced to next
    // lesson after the student completed the previous one), show it from the activity log.
    // Only mark completed if all topics are actually at secure — a partial session is pending.
    if (doneToday.value.lesson && !out.some(t => t.type === "continue")) {
      const lesson = completedLessonToday.value;
      const fullyDone = lesson ? isLessonReady(lesson) : false;
      out.push({
        type: "continue",
        priority: TASK_PRIORITIES.continue,
        xp: XP_BY_TASK.continue,
        stars: 3,
        title: fullyDone ? (lesson?.title || "Lesson") : `Continue: ${lesson?.title || "Lesson"}`,
        subtitle: fullyDone ? "Completed today!" : "Keep going — more topics to do!",
        lessonId: lesson?.id,
        route: lesson ? `/student/${childId}/learn/${lesson.id}` : `/student/${childId}`,
        completed: fullyDone
      });
    }

    // 2. Skipped lesson — show even alongside a continueLesson so it doesn't get buried
    if (out.length < 4 && !doneToday.value.lesson) {
      const skipped = getFirstSkippedLesson();
      if (skipped && !out.some(t => t.lessonId === skipped.lessonId)) {
        out.push({
          type: "newLesson",
          priority: TASK_PRIORITIES.weakTopic, // high priority — treat like a gap to fill
          xp: XP_BY_TASK.newLesson,
          stars: 3,
          title: skipped.lessonTitle,
          subtitle: "Missed — catch up!",
          lessonId: skipped.lessonId,
          route: `/student/${childId}/learn/${skipped.lessonId}`,
          completed: false
        });
      }
    }

    // 3. New lesson from curriculum (if no continue task and no skipped lesson already covers it)
    const hasContinueTask = out.some(t => t.type === "continue");
    const hasNewLessonTask = out.some(t => t.type === "newLesson");
    if (out.length < 4 && remaining.lessons > 0 && !doneToday.value.lesson && !hasContinueTask && !hasNewLessonTask) {
      const suggested = getNextCurriculumLesson();
      if (suggested) {
        out.push({
          type: "newLesson",
          priority: TASK_PRIORITIES.newLesson,
          xp: XP_BY_TASK.newLesson,
          stars: 3,
          title: suggested.lessonTitle,
          subtitle: suggested.reason || "Next in curriculum",
          lessonId: suggested.lessonId,
          route: `/student/${childId}/learn/${suggested.lessonId}`,
          completed: false
        });
      }
    }

    // 4. Revision session (if student has practiced topics and no revision done in 3+ days)
    if (out.length < 4 && !doneToday.value.revision && revisionDue.value && hasPracticedTopics.value) {
      out.push({
        type: "revision",
        priority: TASK_PRIORITIES.revision,
        xp: XP_BY_TASK.revision,
        stars: 2,
        title: "Revision Session",
        subtitle: "Time to revise!",
        route: `/student/${childId}/revision`,
        completed: false
      });
    }
    // 4b. Show completed revision if done today
    if (doneToday.value.revision && !out.some(t => t.type === "revision")) {
      out.push({
        type: "revision",
        priority: TASK_PRIORITIES.revision,
        xp: XP_BY_TASK.revision,
        stars: 2,
        title: "Revision Session",
        subtitle: "Completed!",
        route: `/student/${childId}/revision`,
        completed: true
      });
    }

    // 5. Skills Check (if due)
    const diagnosticDue = assessmentStore.isDiagnosticDue?.(childId);
    if (out.length < 4 && diagnosticDue && !doneToday.value.diagnostic) {
      out.push({
        type: "diagnostic",
        priority: TASK_PRIORITIES.diagnostic,
        xp: XP_BY_TASK.diagnostic,
        stars: 2,
        title: "Skills Check",
        subtitle: "See how much you've improved!",
        route: `/student/${childId}/skills-check`,
        completed: false
      });
    }
    // 5b. Show completed diagnostic if done today
    if (doneToday.value.diagnostic && !out.some(t => t.type === "diagnostic")) {
      out.push({
        type: "diagnostic",
        priority: TASK_PRIORITIES.diagnostic,
        xp: XP_BY_TASK.diagnostic,
        stars: 2,
        title: "Skills Check",
        subtitle: "Completed!",
        route: `/student/${childId}/skills-check`,
        completed: true
      });
    }

    // Sort by priority and limit to 4
    return out.sort((a, b) => a.priority - b.priority).slice(0, 4);
  });

  const totalXPToday = computed(() => {
    return tasks.value
      .filter(t => !t.completed)
      .reduce((sum, t) => sum + t.xp, 0);
  });

  const statusMessage = computed(() => {
    const incomplete = tasks.value.filter(t => !t.completed).length;
    if (incomplete === 0) return "All done for today! Great work!";
    if (incomplete === 1) return "One more task to go!";
    return `${incomplete} tasks ready for you today`;
  });

  return {
    tasks,
    doneToday,
    totalXPToday,
    statusMessage
  };
}
